import { ArrowDropDownRounded, SearchRounded } from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    ClickAwayListener,
    InputAdornment,
    Paper,
    Popper,
    Stack,
    TextField,
    TextFieldProps,
    Typography,
} from '@mui/material';
import {
    parsePhoneNumberFromString,
    getCountries,
    getCountryCallingCode,
    parseIncompletePhoneNumber,
    CountryCode,
} from 'libphonenumber-js/max';
import { useState } from 'react';
import { detectPhoneCountry } from '@/utils/detect-phone-country';
import { useTranslation } from 'react-i18next';

export default function PhoneTextField({
    value,
    initialCountry,
    onChange,
    ...otherProps
}: TextFieldProps & {
    value: string;
    /** Overrides the guess made from the browser. Only pass it when the caller truly knows better. */
    initialCountry?: CountryCode;
    onChange: (value: string) => void;
}) {
    const { t, i18n } = useTranslation();
    const phoneNumberParsed = parsePhoneNumberFromString(value);
    const [phoneNumber, setPhoneNumber] = useState(() => {
        if (phoneNumberParsed?.isValid()) {
            return phoneNumberParsed.formatInternational();
        }
        return parseIncompletePhoneNumber(value) || value;
    });

    const [isValid, setIsValid] = useState(value !== '' ? Boolean(phoneNumberParsed?.isValid()) : true);
    // An already-saved number is authoritative, so it wins over any guess.
    const [country, setCountry] = useState(
        () => phoneNumberParsed?.country || initialCountry || detectPhoneCountry(i18n.language),
    );

    const phoneNumberChange = (newValue: string) => {
        const phoneNumberNormalized = parseIncompletePhoneNumber(newValue);
        const phoneNumberParsed = parsePhoneNumberFromString(phoneNumberNormalized, country);
        if (phoneNumberParsed) {
            setPhoneNumber(phoneNumberParsed.formatInternational());
            if (!phoneNumberParsed.country) {
                const [possibleCountry] = phoneNumberParsed.getPossibleCountries();
                if (possibleCountry) {
                    setCountry(possibleCountry);
                }
            }
            if (phoneNumberParsed.country && phoneNumberParsed.country !== country) {
                setCountry(phoneNumberParsed.country);
            }
            setIsValid(phoneNumberParsed.isValid());
        } else {
            setPhoneNumber(phoneNumberNormalized);
            setIsValid(false);
        }

        onChange(phoneNumberParsed?.formatInternational() || phoneNumberNormalized);
    };

    const phoneCountryChange = (newCountry: CountryCode) => {
        const phoneNumberNormalized = parsePhoneNumberFromString(phoneNumber);
        if (phoneNumberNormalized) {
            try {
                const nationalNumber = phoneNumberNormalized.formatNational();
                const newPhoneNumber = `+${getCountryCallingCode(newCountry)}${nationalNumber}`;
                const phoneNumberParsed = parsePhoneNumberFromString(newPhoneNumber, newCountry);
                // Change the phone number only if the country is different
                if (!phoneNumberParsed?.country || phoneNumberParsed?.country === newCountry) {
                    phoneNumberChange(newPhoneNumber);
                    return;
                }
            } catch (error) {
                // Fallback: clear the phone number if conversion fails
                setPhoneNumber('');
                setIsValid(false);
                onChange('');
            }
        }
        // If the phone number is empty, just set the country
        setCountry(newCountry);
    };

    return (
        <TextField
            label={t('str.phone-number.label')}
            placeholder={t('str.phone-number.placeholder')}
            {...otherProps}
            value={phoneNumber}
            onChange={(e) => {
                phoneNumberChange(e.target.value);
            }}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <CountrySelect
                                key={`phone-selector-${country}`}
                                country={country}
                                onChange={phoneCountryChange}
                            />
                        </InputAdornment>
                    ),
                },
            }}
            error={otherProps.error || !isValid}
            helperText={otherProps.helperText || (!isValid ? t('str.phone-number.invalid') : undefined)}
        />
    );
}

const phoneCountries = getCountries();

const CountrySelect = ({
    country,
    onChange,
}: {
    country: CountryCode;
    onChange: (newCountry: CountryCode) => void;
}) => {
    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [inputValue, setInputValue] = useState('');
    const [inputCountry, setInputCountry] = useState(country);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setInputValue('');
    };

    const handleFocus = (event: React.FocusEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setInputValue('');
    };

    const handleClose = () => {
        if (anchorEl) {
            anchorEl.focus();
        }
        setInputValue('');
        setAnchorEl(null);
    };

    const getFlagCode = (countryCode: string): string => {
        const code = countryCode.toLowerCase();
        // Special case for Ascension Island and Tristan da Cunha
        // Both use the same flag code 'sh' (Saint Helena)
        return ['ac', 'ta'].includes(code) ? 'sh' : code;
    };

    const open = Boolean(anchorEl);
    const id = open ? 'country-phone-code' : undefined;

    const flagCountryCode = getFlagCode(inputCountry);

    return (
        <>
            <Stack
                tabIndex={0}
                direction={'row'}
                onFocus={handleFocus}
                onClick={handleClick}
                gap={0.5}
                alignItems={'center'}
                sx={{ cursor: 'pointer' }}
            >
                <img
                    loading="lazy"
                    width={20}
                    src={`https://flagcdn.com/${flagCountryCode}.svg`}
                    alt={`${country} flag`}
                    onError={(e) => {
                        e.currentTarget.style.display = 'none';
                    }}
                />
                <ArrowDropDownRounded />
            </Stack>
            <Popper
                id={id}
                open={open}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={(theme) => ({ width: '300px', zIndex: theme.zIndex.modal })}
            >
                <Paper elevation={4} sx={{ my: 2 }}>
                    <ClickAwayListener onClickAway={handleClose}>
                        <Autocomplete
                            open
                            onClose={(event, reason) => {
                                if (reason === 'escape' || reason === 'blur') {
                                    handleClose();
                                }
                            }}
                            inputValue={inputValue}
                            onInputChange={(event, newInputValue) => {
                                setInputValue(newInputValue);
                            }}
                            value={inputCountry}
                            onChange={(event, newValue, reason) => {
                                if (
                                    event.type === 'keydown' &&
                                    ((event as React.KeyboardEvent).key === 'Backspace' ||
                                        (event as React.KeyboardEvent).key === 'Delete') &&
                                    reason === 'removeOption'
                                ) {
                                    return;
                                }
                                setInputCountry(newValue);
                                onChange(newValue);
                                handleClose();
                            }}
                            disableCloseOnSelect
                            noOptionsText={t('str.phone-select.country-no-options')}
                            getOptionLabel={(option) => {
                                const countryName = t(`country.${option.toLowerCase()}`, {
                                    ns: 'countries',
                                });
                                return `${countryName}  +${getCountryCallingCode(option)}`;
                            }}
                            renderOption={(props, option) => {
                                const { key, ...optionProps } = props;

                                const callingCode = getCountryCallingCode(option);

                                const flagCountryCode = getFlagCode(option);

                                const countryName = t(`country.${option.toLowerCase()}`, { ns: 'countries' });
                                return (
                                    <Box
                                        key={key}
                                        component="li"
                                        sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                                        {...optionProps}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent={'space-between'}
                                            gap={1}
                                            width={1}
                                            paddingX={1}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                minWidth={0}
                                                maxWidth={1}
                                                width={1}
                                                gap={1}
                                            >
                                                <img
                                                    loading="lazy"
                                                    width={20}
                                                    src={`https://flagcdn.com/${flagCountryCode}.svg`}
                                                    alt={`${country} flag`}
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = 'none';
                                                    }}
                                                />
                                                <Typography noWrap>{countryName}</Typography>
                                            </Stack>
                                            <Typography
                                                variant="caption"
                                                color="textDisabled"
                                            >{`+${callingCode}`}</Typography>
                                        </Stack>
                                    </Box>
                                );
                            }}
                            disableClearable
                            forcePopupIcon={false}
                            options={phoneCountries}
                            renderInput={(params) => (
                                <TextField
                                    autoFocus
                                    {...params}
                                    slotProps={{
                                        input: {
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchRounded fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            )}
                            slots={{
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                popper: ({ placement, children, anchorEl, disablePortal, ...other }) => {
                                    return (
                                        <Box {...other}>
                                            {typeof children === 'function'
                                                ? children({ placement: placement || 'bottom-start' })
                                                : children}
                                        </Box>
                                    );
                                },
                            }}
                        />
                    </ClickAwayListener>
                </Paper>
            </Popper>
        </>
    );
};
